import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import {
  BaseTransactionDetailsDto,
  GetRechnungDetailsDto,
  PatientBillInfoDto,
  DebitPermissionDto,
  ExecutiveDashboardDto,
  KpiAnalyticsDto,
  PayerPerformanceDto,
  RxTypeDistributionDto,
  TrendAnalyticsResponseDto,
  BillingAnalyticsQueryDto,
  UnpaidBillsBadgeDto,
  TicketDto,
  ResponseExportInfoDto,
  GetInsuranceTransactionDto,
  RxBillingTimelineDto,
  RxFeeWaiversDto,
} from "../../api-client";
import { transactionKeys } from "./transaction.keys";

/**
 * Replaces: patientBillInfo function from TransactionStore
 * Used by: PatientBillTable componet
 * @param patientId
 * @returns UseQueryResult<{billInfo: PatientBillInfoDto[];}, Error>
 */
export const usePatientBillsInfo = (patientId: number) => {
  const { transaction } = useApi();

  return useQuery<PatientBillInfoDto[]>({
    queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
    queryFn: () => transaction.patientBillInfo(patientId), // return raw array
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev, // keeps previous data during refetch
    select: (bills) =>
      [...bills].sort((a, b) => {
        const [prefixA, suffixA] = a.rxNumber.split("-");
        const [prefixB, suffixB] = b.rxNumber.split("-");
        return prefixA === prefixB
          ? Number(suffixA) - Number(suffixB)
          : prefixA.localeCompare(prefixB);
      }),
  });
};

/**
 * Replaces: getTransactionByPatientId from TransactionsStore
 * Used by: PatientAllTransactionTable component
 * @param patientID
 * @returns UseQueryResult<{transactions: BaseTransactionDetailsDto[];}, Error>
 */
export const usePatientTransactions = (patientId: number) => {
  const { transaction } = useApi();

  return useQuery<BaseTransactionDetailsDto[]>({
    queryKey: transactionKeys.patientTransactions.forPatient(patientId),
    queryFn: () => transaction.getAllTxsForPatient(patientId),
    enabled: !!patientId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
    select: (txs) =>
      [...txs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
  });
};

/**
 * Replaces: patientRechnungInfo from TransactionsStore
 * Used by: PatientRechnungTable component
 */
export const usePatientRechnungen = (patientId: number) => {
  const { transaction } = useApi();

  return useQuery<GetRechnungDetailsDto[]>({
    queryKey: transactionKeys.patientRechnungen.forPatient(patientId),
    queryFn: () => transaction.patientRechnungInfo(patientId),
    enabled: !!patientId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
    select: (rows) =>
      [...rows].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
  });
};

/**
 * Replaces: getDebitPermissionsByPatient from TransactionsStore
 * Used by: PatientBillingPage for SEPA debit permissions
 */
export const useDebitPermissions = (patientId: number, enabled = true) => {
  const { transaction } = useApi();

  return useQuery<DebitPermissionDto[]>({
    queryKey: transactionKeys.debitPermissions.forPatient(patientId),
    queryFn: () => transaction.getDebitPermissionsForPatient(patientId),
    enabled: enabled && !!patientId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
};

/**
 * Get credit permissions for a patient (for refund operations)
 * Similar to useDebitPermissions but for SEPA credit transfers (refunds)
 */
export const useCreditPermissions = (patientId: number, enabled = true) => {
  const { transaction } = useApi();

  return useQuery<DebitPermissionDto[]>({
    queryKey: transactionKeys.creditPermissions.forPatient(patientId),
    queryFn: () => transaction.getCreditPermissionsForPatient(patientId),
    enabled: enabled && !!patientId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

/** * New Hook
 * Used by: PatientLayoutContainer for unpaid badge count
 */
export const useUnpaidBadgeCount = (patientId: number, enabled: boolean = true) => {
  const { transaction } = useApi();

  return useQuery<UnpaidBillsBadgeDto>({
    queryKey: transactionKeys.unpaidBadgeCount.forPatient(patientId),
    queryFn: () => transaction.badgesForUnpaidBills(patientId),
    enabled: enabled && !!patientId,
    staleTime: 10 * 1000, // 10 seconds cache
  });
};

/**
 * Fetches the complete billing timeline for a single Rx.
 * Returns all transactions (Cash, EC, LastSchrift, BankTransfer) and
 * Rechnungen with their BillReminders, sorted chronologically.
 *
 * Used by: PatientBillDetailsModal (row click in PatientBillTable)
 */
export const useRxBillingTimeline = (rxId: string, enabled = true) => {
  const { transaction } = useApi();

  return useQuery<RxBillingTimelineDto>({
    queryKey: transactionKeys.rxBillingTimeline.forRx(rxId),
    queryFn: () => transaction.getRxBillingTimeline(rxId),
    enabled: enabled && !!rxId,
    staleTime: 2 * 60 * 1000, // 2 minutes cache
    refetchOnWindowFocus: false,
  });
};

/**
 * The automatically added fees of an Rx that may be waived (today: the private
 * assessment fee), whether each is waived, and whether a live Rechnung locks them.
 * Used by: PatientBillDetailsModalShad (RxFeeWaivers)
 */
export const useRxFeeWaivers = (rxId: string, enabled = true) => {
  const { transaction } = useApi();

  return useQuery<RxFeeWaiversDto>({
    queryKey: transactionKeys.rxFeeWaivers.forRx(rxId),
    queryFn: () => transaction.getRxFeeWaivers(rxId),
    enabled: enabled && !!rxId,
    staleTime: 0, // a Rechnung created elsewhere locks the waiver
    refetchOnWindowFocus: false,
  });
};

/**
 *
 */
export const useGetRxDebitByRxId = (rxId: string, patientId: number) => {
  const { rx } = useApi();

  return useQuery<any>({
    queryKey: transactionKeys.rxDebit.forRx(rxId),
    queryFn: async () => await rx.getRxDebitById(rxId),
    enabled: true,
    staleTime: 0,
  });
};

export const useGetRxCreditByRxId = (rxId: string, patientId: number) => {
  const { rx } = useApi();

  return useQuery<any>({
    queryKey: transactionKeys.creditPermissions.forPatient(patientId),
    queryFn: async () => await rx.getRxDebitById(rxId), // Reuse same endpoint, returns patient info
    enabled: true,
    staleTime: 0,
  });
};

export const useDuePaymentByRxId = (
  rxId: string,
  patientId: number,
  isSingleUse: boolean,
  enabled = true,
) => {
  const { transaction } = useApi();
  return useQuery({
    queryKey: transactionKeys.duePaymentByRx.forRx(rxId),
    queryFn: () => transaction.getDuePaymentByRxId(rxId),
    enabled: enabled && isSingleUse,
  });
};

export const useGetUniquePairsForPatient = (patientId: number, enabled = true) => {
  const { transaction } = useApi();

  return useQuery({
    queryKey: transactionKeys.uniqueDebitPermissionPairs.forPatient(patientId),
    queryFn: () => transaction.getUniquePairsForPatient(patientId),
    enabled: enabled && !!patientId,
  });
};

// ========================= ANALYTICS QUERIES ========================= //

/**
 * Executive Dashboard Analytics
 * Used by: Executive Dashboard page/component
 * @param query BillingAnalyticsQueryDto - contains filters like accountId, dateFrom, dateTo, etc.
 * @param enabled
 * @returns UseQueryResult<ExecutiveDashboardDto, Error>
 */
export const useExecutiveDashboard = (query: BillingAnalyticsQueryDto, enabled = true) => {
  const { transaction } = useApi();

  return useQuery<ExecutiveDashboardDto>({
    queryKey: transactionKeys.executiveDashboard.withFilters(query),
    queryFn: () =>
      transaction.getExecutiveDashboard(
        query.startDate,
        query.endDate,
        query.accountId,
        query.payerId,
        query.billStatus,
        query.billType,
        query.userId,
      ),
    enabled: enabled && !!query.accountId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
};

/**
 * KPI Analytics with period comparison
 * Used by: KPI dashboard widgets, analytics page
 * @param query BillingAnalyticsQueryDto
 * @param enabled
 * @returns UseQueryResult<KpiAnalyticsDto, Error>
 */
export const useKpiAnalytics = (query: BillingAnalyticsQueryDto, enabled = true) => {
  const { transaction } = useApi();

  return useQuery<KpiAnalyticsDto>({
    queryKey: transactionKeys.kpiAnalytics.withFilters(query),
    queryFn: () =>
      transaction.getKpiAnalytics(
        query.startDate,
        query.endDate,
        query.accountId,
        query.payerId,
        query.billStatus,
        query.billType,
        query.userId,
      ),
    enabled: enabled,
    staleTime: 3 * 60 * 1000, // 3 minutes
    placeholderData: (prev) => prev,
  });
};

/**
 * Top Performing Payers by Amount
 * Used by: Analytics dashboards, reports
 * @param query BillingAnalyticsQueryDto
 * @param limit number - max number of top payers to return (default: 10)
 * @param enabled
 * @returns UseQueryResult<PayerPerformanceDto[], Error>
 */
export const useTopPayersByAmount = (
  query: BillingAnalyticsQueryDto,
  limit: number = 10,
  enabled = true,
) => {
  const { transaction } = useApi();

  return useQuery<PayerPerformanceDto[]>({
    queryKey: transactionKeys.topPayersByAmount.withFilters(query, limit),
    queryFn: () =>
      transaction.getTopPayersByAmount(
        query.startDate,
        query.endDate,
        query.accountId,
        query.payerId,
        query.billStatus,
        query.billType,
        query.userId,
        limit,
      ),
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (prev) => prev,
    select: (payers) => [...payers].sort((a, b) => b.totalAmount - a.totalAmount),
  });
};

/**
 * Prescription Type Distribution
 * Used by: Analytics charts, distribution reports
 * @param query BillingAnalyticsQueryDto
 * @param enabled
 * @returns UseQueryResult<RxTypeDistributionDto[], Error>
 */
export const useRxTypeDistribution = (query: BillingAnalyticsQueryDto, enabled = true) => {
  const { transaction } = useApi();

  return useQuery<RxTypeDistributionDto[]>({
    queryKey: transactionKeys.rxTypeDistribution.withFilters(query),
    queryFn: () =>
      transaction.getRxTypeDistribution(
        query.startDate,
        query.endDate,
        query.accountId,
        query.payerId,
        query.billStatus,
        query.billType,
        query.userId,
      ),
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (prev) => prev,
  });
};

/**
 * Comprehensive Trend Analytics
 * Used by: Trend charts, time-series analysis, forecasting
 * @param query BillingAnalyticsQueryDto
 * @param enabled
 * @returns UseQueryResult<TrendAnalyticsResponseDto, Error>
 */
export const useTrendAnalytics = (query: BillingAnalyticsQueryDto, enabled = true) => {
  const { transaction } = useApi();

  return useQuery<TrendAnalyticsResponseDto>({
    queryKey: transactionKeys.trendAnalytics.withFilters(query),
    queryFn: () =>
      transaction.getTrendAnalytics(
        query.startDate,
        query.endDate,
        query.accountId,
        query.payerId,
        query.billStatus,
        query.billType,
        query.userId,
      ),
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (prev) => prev,
  });
};

// ========================= INSURANCE EXPORT QUERIES ========================= //

/**
 * Fetches insurance export data for a given transaction Id
 * Used by: Insurance export components
 * @param transactionId string
 * @returns UseQueryResult<ResponseExportInfoDto, Error>
 */
export const useExportedInsuranceDetails = (transactionId: string) => {
  const { transaction } = useApi();
  return useQuery<ResponseExportInfoDto>({
    queryKey: transactionKeys.insuranceExport.forTransactionId(transactionId),
    queryFn: () => transaction.exportedInsuranceDetails(transactionId),
    enabled: !!transactionId,
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
};

/**
 * Fetches EDIFACT payload for a specific export (lazy loaded)
 * Used by: EdifactColumnPlaceholder component
 * @param exportId string - The insurance export record ID
 * @param enabled boolean - controls when the query runs
 * @returns UseQueryResult<string, Error>
 */
export const useEdifactPayload = (exportId: string, enabled: boolean = false) => {
  const { transaction } = useApi();

  return useQuery<string>({
    queryKey: transactionKeys.edifactPayload.forExport(exportId),
    queryFn: () => transaction.getEdifactPayload(exportId),
    enabled: enabled && !!exportId,
    staleTime: 10 * 60 * 1000, // 10 minutes - EDIFACT doesn't change
    placeholderData: (prev) => prev,
  });
};

/**
 * Used in the involvement.tsx file create chip and link to the Export Modal directly
 * @param transactionId
 * @returns
 */
export const useMinimalInsuranceTransactionbyId = (transactionId: string) => {
  const { transaction } = useApi();

  return useQuery<GetInsuranceTransactionDto>({
    queryKey: transactionKeys.transaction.details(transactionId),
    queryFn: () => transaction.minimalInsuranceTransactionById(transactionId),
    enabled: !!transactionId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
};

/**
 * Hook to fetch tickets associated with an insurance transaction
 * @param transactionId - The insurance transaction ID
 * @returns UseQueryResult<TicketDto[], Error>
 */
export const useTransactionTickets = (transactionId: string) => {
  const { transaction } = useApi();

  return useQuery<TicketDto[]>({
    queryKey: transactionKeys.transactionTickets.forTransaction(transactionId),
    queryFn: () => transaction.allTicketsByTransactionId(transactionId),
    enabled: !!transactionId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
};
